import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export interface ToastOptions {
  title?: string;
  duration?: number;
  position?: 'toast-top-right' | 'toast-top-left' | 'toast-bottom-right' | 'toast-bottom-left' | 'toast-top-center' | 'toast-bottom-center';
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  private translateMessage(message?: string | null): string | undefined {
    return message ? this.translate.instant(message) : undefined;
  }

  /**
   * Show a success toast notification
   * @param message - The message to display
   * @param options - Optional configuration for the toast
   */
  success(message: string, options?: ToastOptions): void {
    const title = options?.title ? this.translate.instant(options.title) : this.translate.instant('TOASTS.SUCCESS');
    const finalMessage = message
  ? this.translate.instant(message): '';
    this.toastr.success(finalMessage, title, {
      timeOut: options?.duration || 3000,
      positionClass: options?.position || 'toast-top-right'
    });
  }

  /**
   * Show a warning toast notification
   * @param message - The message to display
   * @param options - Optional configuration for the toast
   */
  warning(message: string, options?: ToastOptions): void {
    const title = options?.title ? this.translate.instant(options.title) : this.translate.instant('TOASTS.INFO');
    this.toastr.warning(this.translate.instant(message), title, {
      timeOut: options?.duration || 4000,
      positionClass: options?.position || 'toast-top-right'
    });
  }

  /**
   * Show an error toast notification
   * @param message - The message to display
   * @param options - Optional configuration for the toast
   */
  error(message: string | null, options?: ToastOptions): void {
    const title = options?.title ? this.translate.instant(options.title) : this.translate.instant('TOASTS.ERROR');
    this.toastr.error(this.translateMessage(message), title, {
      timeOut: options?.duration || 5000,
      positionClass: options?.position || 'toast-top-right'
    });
  }

  /**
   * Show an info toast notification
   * @param message - The message to display
   * @param options - Optional configuration for the toast
   */
  info(message: string, options?: ToastOptions): void {
    const title = options?.title ? this.translate.instant(options.title) : this.translate.instant('TOASTS.INFO');
    this.toastr.info(this.translate.instant(message), title, {
      timeOut: options?.duration || 3000,
      positionClass: options?.position || 'toast-top-right'
    });
  }

  /**
   * Clear all active toast notifications
   */
  clear(): void {
    this.toastr.clear();
  }
}
